import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import {
  useFeatureFlags,
  useSaveFeatureFlags,
  useTenantAnalyticsConfig,
  useSaveTenantAnalyticsConfig,
} from "../../lib/api";
import { FieldLabel, GhostButton, PrimaryButton, SegmentButton, ToggleRow, inputClass } from "./primitives";

const PAGE_FLAGS = [
  { key: "page_overview", label: "Overview" },
  { key: "page_analytics", label: "Workshop Analytics" },
  { key: "page_journey", label: "Service Journey" },
  { key: "page_book_service", label: "Book a Service" },
  { key: "page_workshop", label: "Workshop" },
  { key: "page_organization", label: "Regions, Dealers, Workshops and People" },
];

const BOOKING_UI = [
  { value: "maruti", label: "Full intake form" },
  { value: "hyundai", label: "Guided short flow" },
];

// The Workshop Analytics "volume" card normally titles itself off the Job
// Card stage's label ("<label> volume", set in Service Journey). This is a
// separate, optional override — the card can read differently from the step
// copy without renaming that stage everywhere else it appears.
function VolumeCardTitleEditor({ tenantId }) {
  const { data, isLoading } = useTenantAnalyticsConfig(tenantId);
  const save = useSaveTenantAnalyticsConfig(tenantId);
  const [value, setValue] = useState("");

  useEffect(() => setValue(data?.volume_card_title || ""), [data]);

  const dirty = (data?.volume_card_title || "") !== value;

  const onSave = async () => {
    try {
      await save.mutateAsync({ volume_card_title: value.trim() || null });
      toast.success("Analytics card title saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    }
  };

  return (
    <div>
      <FieldLabel>Workshop Analytics card title</FieldLabel>
      <input
        type="text"
        className={inputClass}
        value={value}
        placeholder="Repair Order volume"
        disabled={isLoading}
        onChange={(e) => setValue(e.target.value)}
      />
      {dirty && (
        <div className="mt-2 flex items-center gap-2">
          <PrimaryButton onClick={onSave} disabled={save.isPending}>
            {save.isPending && <Loader2 className="animate-spin" size={14} />}
            Save
          </PrimaryButton>
          <GhostButton onClick={() => setValue(data?.volume_card_title || "")}>Discard</GhostButton>
        </div>
      )}
    </div>
  );
}

// The Unanswered Customer Messages card's segmented toggle normally reads
// "Per <role short name>" for the SA and SM roles (Roles & access). These are
// separate, optional overrides for that toggle's copy specifically, without
// renaming either role everywhere else it appears.
function ResponseDelayLabelsEditor({ tenantId }) {
  const { data, isLoading } = useTenantAnalyticsConfig(tenantId);
  const save = useSaveTenantAnalyticsConfig(tenantId);
  const [saLabel, setSaLabel] = useState("");
  const [smLabel, setSmLabel] = useState("");

  useEffect(() => {
    setSaLabel(data?.response_delay_sa_label || "");
    setSmLabel(data?.response_delay_sm_label || "");
  }, [data]);

  const dirty =
    (data?.response_delay_sa_label || "") !== saLabel || (data?.response_delay_sm_label || "") !== smLabel;

  const onSave = async () => {
    try {
      await save.mutateAsync({
        response_delay_sa_label: saLabel.trim() || null,
        response_delay_sm_label: smLabel.trim() || null,
      });
      toast.success("Response delay toggle labels saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    }
  };

  const onDiscard = () => {
    setSaLabel(data?.response_delay_sa_label || "");
    setSmLabel(data?.response_delay_sm_label || "");
  };

  return (
    <div>
      <FieldLabel>Unanswered Customer Messages toggle labels</FieldLabel>
      <div className="flex gap-3">
        <div className="flex-1">
          <input
            type="text"
            className={inputClass}
            value={saLabel}
            placeholder="Per SA"
            disabled={isLoading}
            onChange={(e) => setSaLabel(e.target.value)}
            aria-label="Per-SA toggle label"
          />
        </div>
        <div className="flex-1">
          <input
            type="text"
            className={inputClass}
            value={smLabel}
            placeholder="Per SM"
            disabled={isLoading}
            onChange={(e) => setSmLabel(e.target.value)}
            aria-label="Per-SM toggle label"
          />
        </div>
      </div>
      {dirty && (
        <div className="mt-2 flex items-center gap-2">
          <PrimaryButton onClick={onSave} disabled={save.isPending}>
            {save.isPending && <Loader2 className="animate-spin" size={14} />}
            Save
          </PrimaryButton>
          <GhostButton onClick={onDiscard}>Discard</GhostButton>
        </div>
      )}
    </div>
  );
}

export default function FeatureFlagsEditor({ tenantId }) {
  const { data, isLoading } = useFeatureFlags(tenantId);
  const save = useSaveFeatureFlags(tenantId);
  const baseline = useMemo(() => data || {}, [data]);
  const [flags, setFlags] = useState({});

  useEffect(() => setFlags(baseline), [baseline]);

  const set = (key, value) => setFlags((f) => ({ ...f, [key]: value }));
  const dirty = JSON.stringify(flags) !== JSON.stringify(baseline);

  const onSave = async () => {
    try {
      await save.mutateAsync(flags);
      toast.success("Feature access saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    }
  };

  if (isLoading) {
    return (
      <div className="mt-7 flex items-center gap-2 text-[13px] text-[#6B7280]">
        <Loader2 className="animate-spin" size={15} /> Loading
      </div>
    );
  }

  return (
    <div className="mt-7 flex max-w-[640px] flex-col gap-6">
      <div>
        <FieldLabel>Pages</FieldLabel>
        <div className="flex flex-col gap-2">
          {PAGE_FLAGS.map(({ key, label }) => (
            <ToggleRow key={key} title={label} checked={flags[key] !== false} onChange={(v) => set(key, v)} />
          ))}
        </div>
      </div>

      <div>
        <FieldLabel>Advisor desk</FieldLabel>
        <ToggleRow
          title="Chat panel"
          checked={flags.desk_chat !== false}
          onChange={(v) => set("desk_chat", v)}
        />
      </div>

      {flags.page_analytics !== false && (
        <>
          <VolumeCardTitleEditor tenantId={tenantId} />
          <ResponseDelayLabelsEditor tenantId={tenantId} />
        </>
      )}

      {flags.page_book_service !== false && (
        <div>
          <FieldLabel>Booking form</FieldLabel>
          <div className="flex gap-2">
            {BOOKING_UI.map(({ value, label }) => (
              <SegmentButton
                key={value}
                active={(flags.book_service_ui || "maruti") === value}
                onClick={() => set("book_service_ui", value)}
              >
                {label}
              </SegmentButton>
            ))}
          </div>
          {flags.book_service_ui === "hyundai" && (
            <div className="mt-3">
              <ToggleRow
                title="Final bill upload required"
                checked={flags.require_final_bill_upload === true}
                onChange={(v) => set("require_final_bill_upload", v)}
              />
            </div>
          )}
        </div>
      )}

      <div>
        <FieldLabel>Delivery follow-up chain</FieldLabel>
        <ToggleRow
          title="Pre-delivery approval starts the follow-up chain"
          checked={flags.pre_delivery_feedback_chain === true}
          onChange={(v) => set("pre_delivery_feedback_chain", v)}
        />
      </div>

      <div>
        <FieldLabel>Service journey</FieldLabel>
        <ToggleRow
          title="Built-in stages follow their Service journey settings"
          checked={flags.journey_engine === true}
          onChange={(v) => set("journey_engine", v)}
        />
      </div>

      <div>
        <FieldLabel>Journeys</FieldLabel>
        <ToggleRow
          title="Pre-IFR Journey escalation"
          checked={flags.pre_ifr_journey === true}
          onChange={(v) => set("pre_ifr_journey", v)}
        />
      </div>

      <div className="flex items-center gap-3">
        <PrimaryButton onClick={onSave} disabled={save.isPending}>
          {save.isPending && <Loader2 className="animate-spin" size={14} />}
          Save changes
        </PrimaryButton>
        {dirty && <GhostButton onClick={() => setFlags(baseline)}>Discard</GhostButton>}
      </div>
    </div>
  );
}
